import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ArrowLeft, ArrowRight, X } from 'lucide-react'
import { recoveryArrival } from '@/lib/auth/auth'
import { requestUserLocation, deepLinkArrival } from '@/lib/roads/userLocation'
import BikeTypeTip from '@/components/ui/BikeTypeTip'

const STORAGE_KEY = 'rr-onboarded-v1'

const DOTS = [
  { color: '#FF4438', label: 'Sport', img: '/images/sport-outline.png' },
  { color: '#34D26B', label: 'ADV', img: '/images/adv-outline.png' },
  { color: '#FF9F1C', label: 'Sumo', img: '/images/sumo-outline.png' },
  { color: '#3B82F6', label: 'Cruiser', img: '/images/cruiser-outline.png' },
]

const STEPS = [
  {
    title: 'Every road worth riding. Rated in one place.',
    visual: null,
  },
  {
    title: 'Color shows the kind of ride. Intensity shows how good it is.',
    visual: 'colorbar' as const,
  },
  {
    title: 'Find your road.\nBuild your ride.\nGo.',
    visual: null,
  },
]

export default function Onboarding() {
  const [visible, setVisible] = useState(false)
  const [step, setStep] = useState(0)
  const [zoomed, setZoomed] = useState(false)

  useEffect(() => {
    // Arriving via a recovery email link goes straight to the change-password
    // flow — the intro would only block it.
    if (recoveryArrival) {
      try {
        localStorage.setItem(STORAGE_KEY, '1')
      } catch {
        /* ignore */
      }
      return
    }
    // Shared road/route links skip the intro panels and the location prompt —
    // the linked road/route is the content. Not marked onboarded, so a
    // first-time visitor still gets the intro on a normal visit later.
    if (deepLinkArrival) return
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        setVisible(true)
      } else {
        // Returning user — no intro is shown, so the geolocation prompt
        // fires immediately on page load.
        requestUserLocation()
      }
    } catch {
      /* storage unavailable — skip onboarding */
      requestUserLocation()
    }
  }, [])

  const finish = () => {
    try {
      localStorage.setItem(STORAGE_KEY, '1')
    } catch {
      /* ignore */
    }
    setVisible(false)
    // Tell the welcome popup it can queue in now that the intro is gone.
    window.dispatchEvent(new Event('rr-intro-done'))
    // Intro closed (skip or ENTER THE MAP) — this tap is a user gesture, so
    // mobile browsers will show the geolocation prompt right away.
    requestUserLocation()
  }

  if (!visible) return null

  const last = step === STEPS.length - 1

  return (
    <>
    {zoomed && (
      <div
        onClick={() => setZoomed(false)}
        className="fixed inset-0 z-[65] bg-black/95 flex items-center justify-center p-4 cursor-zoom-out"
      >
        <img
          src="/images/ebr-wheelie.JPG"
          alt="Rudy having some fun on his 2014 EBR 1190RX. Photo by crownmoto"
          className="max-h-full max-w-full object-contain"
        />
      </div>
    )}
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6">
      <button
        onClick={finish}
        className="absolute top-4 right-4 min-h-11 px-4 text-sm text-white/50 hover:text-white flex items-center gap-1.5"
      >
        SKIP <X size={14} />
      </button>

      <div className="max-w-xl w-full text-center space-y-5">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            transition={{ duration: 0.3 }}
            className="space-y-8"
          >
            {/* Fixed-height slots: title and visual reserve the same space on
                every step so the button below never shifts between panels. */}
            <div className="flex items-center justify-center h-[140px] md:h-[180px]">
              <h1
                className={`text-3xl md:text-5xl font-bold tracking-tight text-white whitespace-pre-line ${
                  last ? 'leading-[2.2]' : 'leading-tight'
                }`}
              >
                {STEPS[step].title}
              </h1>
            </div>

            <div className="h-44 flex items-end justify-center">
              {step === 0 && (
                <img
                  src="/images/roadratings-logo-2.png"
                  alt="RoadRatings logo"
                  className="self-end h-[220px] w-auto max-w-[90%] object-contain object-bottom"
                />
              )}
              {STEPS[step].visual === 'colorbar' && (
                <div className="space-y-4">
                  <div className="flex justify-center">
                    {DOTS.map((d) => (
                      <BikeTypeTip key={d.label} type={d.label}>
                        <div className="w-16 flex flex-col items-center gap-[3px] cursor-default">
                          <img
                            src={d.img}
                            alt=""
                            className="h-10 w-10 object-contain"
                          />
                            <span className="text-[15px] font-mono uppercase tracking-wider text-white">
                              {d.label}
                            </span>
                          <span className="w-10 h-[3px] rounded-full" style={{ background: d.color }} />
                        </div>
                      </BikeTypeTip>
                    ))}
                  </div>
                  <div className="mx-auto w-64 h-2 rounded-full bg-gradient-to-r from-amber-400 via-white/60 to-white/20" />
                  <div className="flex justify-between w-64 mx-auto text-[12px] font-mono uppercase tracking-wider text-white">
                    <span>vivid · best</span>
                    <span>muted · average</span>
                  </div>
                </div>
              )}
              {step === STEPS.length - 1 && (
                <img
                  src="/images/ebr-wheelie.JPG"
                  alt="Rudy having some fun on his 2014 EBR 1190RX. Photo by crownmoto"
                  onClick={() => setZoomed(true)}
                  className="max-h-44 max-w-[84%] rounded-lg object-cover cursor-zoom-in"
                />
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="space-y-4">
          <div className="flex justify-center gap-2">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? 'w-6 bg-amber-400' : 'w-1.5 bg-white/25'
                }`}
              />
            ))}
          </div>

          <div className="flex flex-col items-center gap-3">
            {last ? (
              <button
                onClick={finish}
                className="h-11 px-8 rounded-lg bg-amber-400 hover:bg-amber-300 text-black font-bold text-sm tracking-widest"
              >
                ENTER THE MAP
              </button>
            ) : (
              <button
                onClick={() => setStep((s) => s + 1)}
                className="h-11 px-6 rounded-lg bg-amber-400 hover:bg-amber-300 text-black font-bold text-sm tracking-widest inline-flex items-center justify-center gap-2"
              >
                NEXT <ArrowRight size={15} />
              </button>
            )}
            <button
              onClick={() => setStep((s) => s - 1)}
              aria-hidden={step === 0}
              tabIndex={step === 0 ? -1 : 0}
              className={`h-8 px-3 rounded-md bg-black border border-amber-400/60 text-amber-400 text-xs font-medium inline-flex items-center justify-center gap-1.5 hover:border-amber-300 hover:text-amber-300 ${
                step === 0 ? 'invisible' : ''
              }`}
            >
              <ArrowLeft size={12} /> Back
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}
